import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, Container, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, Card, CardContent, Fade, Avatar,
    IconButton, TextField, InputAdornment, LinearProgress,
    Tooltip, CircularProgress, Divider, Zoom, Breadcrumbs, Link
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Class as ClassIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    AccessTime as LateIcon,
    Group as GroupIcon,
    CalendarMonth as CalendarIcon,
    Search as SearchIcon,
    TrendingUp as StatsIcon,
    PersonOff as AbsentIcon,
    PersonAdd as PresentIcon,
    History as HistoryIcon,
    Save as SaveIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../../context/ToastContext';
import { CustomNepaliDatePicker, getCurrentBSDate } from '../../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;
const float = keyframes`0% { transform: translateY(0px); } 50% { transform: translateY(-10px); } 100% { transform: translateY(0px); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 80px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    margin-bottom: 30px;
`;

const ClassCard = styled(Card)`
    border-radius: 24px !important;
    border: 1px solid #e2e8f0 !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    height: 100%;
    cursor: pointer;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        border-color: #6366f1 !important;
    }
`;

const StyledTableCell = styled(TableCell)`
    font-weight: 700 !important;
    color: #475569 !important;
    background-color: #f8fafc !important;
    border-bottom: 2px solid #e2e8f0 !important;
    text-transform: uppercase;
    font-size: 0.75rem !important;
    letter-spacing: 0.05em;
`;

const StatCard = styled(Paper)`
    padding: 20px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const StatusButton = styled(Button)`
    border-radius: 12px !important;
    text-transform: none !important;
    font-weight: 700 !important;
    padding: 6px 12px !important;
    min-width: 80px !important;
    transition: all 0.2s !important;
    
    &.status-Present {
        background-color: ${props => props.$active ? '#10b981' : 'transparent'} !important;
        color: ${props => props.$active ? 'white' : '#10b981'} !important;
        border: 1px solid #10b981 !important;
    }
    
    &.status-Absent {
        background-color: ${props => props.$active ? '#ef4444' : 'transparent'} !important;
        color: ${props => props.$active ? 'white' : '#ef4444'} !important;
        border: 1px solid #ef4444 !important;
    }
    
    &.status-Late {
        background-color: ${props => props.$active ? '#f59e0b' : 'transparent'} !important;
        color: ${props => props.$active ? 'white' : '#f59e0b'} !important;
        border: 1px solid #f59e0b !important;
    }
`;

const ClassAttendance = () => {
    const { currentUser } = useSelector((state) => state.user);
    const { showToast } = useToast();
    const location = useLocation();

    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [students, setStudents] = useState([]);
    const [attendance, setAttendance] = useState({});
    const [selectedDate, setSelectedDate] = useState(getCurrentBSDate());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const schoolId = currentUser?._id;

    useEffect(() => {
        if (schoolId) {
            fetchClasses();
        }
    }, [schoolId]);

    useEffect(() => {
        if (selectedClass && selectedDate) {
            fetchStudentsAndAttendance();
        }
    }, [selectedClass, selectedDate]);

    useEffect(() => {
        if (location.state && location.state.autoSelectClass && classes.length > 0) {
            const cls = classes.find(c => c._id === location.state.autoSelectClass);
            if (cls) setSelectedClass(cls);
        }
    }, [location.state, classes]);

    const fetchClasses = async () => {
        setLoading(true);
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/SclassList/${schoolId}`);
            if (Array.isArray(result.data)) {
                setClasses(result.data);
            }
        } catch (error) {
            console.error('Error fetching classes:', error);
            showToast('Error loading classes', 'error');
        }
        setLoading(false);
    };

    const fetchStudentsAndAttendance = async () => {
        setLoading(true);
        try {
            const studentRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/Sclass/Students/${selectedClass._id}`);
            const studentsList = Array.isArray(studentRes.data) ? studentRes.data : [];
            setStudents(studentsList);

            const attendanceRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/ClassAttendance/${selectedClass._id}/${selectedDate}`);
            const existingAttendance = attendanceRes.data || {};
            
            const finalAttendance = {};
            studentsList.forEach(student => {
                finalAttendance[student._id] = existingAttendance[student._id] || 'Not Marked';
            });
            setAttendance(finalAttendance);
        } catch (error) {
            console.error('Error fetching data:', error);
            showToast('Error loading attendance data', 'error');
        }
        setLoading(false);
    };

    const handleAttendanceChange = (studentId, status) => {
        setAttendance(prev => ({ ...prev, [studentId]: status }));
    };

    const handleSaveAttendance = async () => {
        setSaving(true);
        try {
            const records = Object.entries(attendance)
                .filter(([_, status]) => status !== 'Not Marked')
                .map(([studentId, status]) => ({
                    studentID: studentId,
                    date: selectedDate,
                    status: status,
                    subName: null
                }));

            await Promise.all(records.map(record => 
                axios.put(`${process.env.REACT_APP_BASE_URL}/StudentAttendance/${record.studentID}`, record)
            ));

            showToast('Attendance updated successfully!', 'success');
        } catch (error) {
            console.error('Error saving attendance:', error);
            showToast('Error updating attendance', 'error');
        }
        setSaving(false);
    };

    const stats = useMemo(() => {
        const total = students.length;
        const present = Object.values(attendance).filter(v => v === 'Present').length;
        const absent = Object.values(attendance).filter(v => v === 'Absent').length;
        const late = Object.values(attendance).filter(v => v === 'Late').length;
        const marked = Object.values(attendance).filter(v => v !== 'Not Marked').length;
        const rate = marked > 0 ? ((present / marked) * 100).toFixed(0) : 0;
        return { total, present, absent, late, marked, rate };
    }, [students, attendance]);

    const filteredStudents = useMemo(() => {
        if (!searchTerm) return students;
        return students.filter(s => 
            s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            s.rollNum?.toString().includes(searchTerm)
        );
    }, [students, searchTerm]);

    const renderClassesView = () => (
        <Fade in timeout={500}>
            <Box>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>
                        Select a Class
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Choose a class to view or manage daily attendance records.
                    </Typography>
                </Box>
                
                <Grid container spacing={3}>
                    {classes.map((cls) => (
                        <Grid item xs={12} sm={6} md={4} key={cls._id}>
                            <ClassCard elevation={0} onClick={() => setSelectedClass(cls)}>
                                <CardContent sx={{ p: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Avatar sx={{ bgcolor: '#eef2ff', color: '#6366f1', width: 56, height: 56 }}>
                                            <ClassIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                                {cls.sclassName}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                                                ACADEMIC RECORD
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ mb: 3, borderStyle: 'dashed' }} />
                                    <Button fullWidth variant="contained" endIcon={<ArrowBackIcon sx={{ transform: 'rotate(180deg)' }} />} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, bgcolor: '#f1f5f9', color: '#1e293b', boxShadow: 'none', '&:hover': { bgcolor: '#e2e8f0', boxShadow: 'none' } }}>
                                        View Roll Call
                                    </Button>
                                </CardContent>
                            </ClassCard>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Fade>
    );

    const renderAttendanceView = () => (
        <Fade in timeout={500}>
            <Box>
                {/* Stats Dashboard */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard elevation={0}>
                            <Avatar sx={{ bgcolor: '#eef2ff', color: '#6366f1' }}><GroupIcon /></Avatar>
                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>STUDENTS</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.total}</Typography>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard elevation={0}>
                            <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981' }}><CheckCircleIcon /></Avatar>
                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PRESENT</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.present}</Typography>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard elevation={0}>
                            <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444' }}><AbsentIcon /></Avatar>
                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>ABSENT</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.absent}</Typography>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard elevation={0}>
                            <Avatar sx={{ bgcolor: '#fff7ed', color: '#f59e0b' }}><StatsIcon /></Avatar>
                            <Box sx={{ width: '100%' }}>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>DAILY RATE</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.rate}%</Typography>
                                    <LinearProgress variant="determinate" value={Number(stats.rate)} sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: '#eef2ff', '& .MuiLinearProgress-bar': { bgcolor: '#10b981' } }} />
                                </Box>
                            </Box>
                        </StatCard>
                    </Grid>
                </Grid>

                {/* Filter & Action Bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField 
                            placeholder="Search student..." 
                            size="small" 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                            sx={{ width: 250, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                        <Box sx={{ width: 200 }}>
                            <CustomNepaliDatePicker
                                value={selectedDate}
                                onChange={(date) => setSelectedDate(date)}
                                label="View Date"
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => setSelectedClass(null)}
                            sx={{ borderRadius: '12px', fontWeight: 700, border: '2px solid #e2e8f0' }}
                        >
                            Change Class
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveAttendance}
                            disabled={saving || students.length === 0}
                            sx={{ borderRadius: '12px', px: 4, bgcolor: '#6366f1', fontWeight: 800 }}
                        >
                            {saving ? 'Saving...' : 'Update Records'}
                        </Button>
                    </Box>
                </Box>

                <PremiumCard sx={{ border: 'none !important' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell sx={{ width: '10%' }}>Roll No</StyledTableCell>
                                    <StyledTableCell sx={{ width: '40%' }}>Student Name</StyledTableCell>
                                    <StyledTableCell sx={{ width: '15%' }}>Status</StyledTableCell>
                                    <StyledTableCell align="right">Modify Status</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                                ) : filteredStudents.length === 0 ? (
                                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 10 }}><Typography color="textSecondary">No records found.</Typography></TableCell></TableRow>
                                ) : filteredStudents.map((student) => {
                                    const currentStatus = attendance[student._id] || 'Not Marked';
                                    return (
                                        <TableRow key={student._id} hover>
                                            <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{student.rollNum}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#f1f5f9', color: '#6366f1', fontWeight: 800 }}>
                                                        {student.name.charAt(0)}
                                                    </Avatar>
                                                    <Typography sx={{ fontWeight: 600 }}>{student.name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={currentStatus} 
                                                    size="small" 
                                                    sx={{ 
                                                        fontWeight: 800, 
                                                        bgcolor: currentStatus === 'Present' ? '#f0fdf4' : (currentStatus === 'Absent' ? '#fef2f2' : (currentStatus === 'Late' ? '#fff7ed' : '#f1f5f9')),
                                                        color: currentStatus === 'Present' ? '#10b981' : (currentStatus === 'Absent' ? '#ef4444' : (currentStatus === 'Late' ? '#f59e0b' : '#64748b'))
                                                    }} 
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                    <Tooltip title="Present">
                                                        <StatusButton $active={currentStatus === 'Present'} className="status-Present" onClick={() => handleAttendanceChange(student._id, 'Present')}>P</StatusButton>
                                                    </Tooltip>
                                                    <Tooltip title="Absent">
                                                        <StatusButton $active={currentStatus === 'Absent'} className="status-Absent" onClick={() => handleAttendanceChange(student._id, 'Absent')}>A</StatusButton>
                                                    </Tooltip>
                                                    <Tooltip title="Late">
                                                        <StatusButton $active={currentStatus === 'Late'} className="status-Late" onClick={() => handleAttendanceChange(student._id, 'Late')}>L</StatusButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </PremiumCard>
            </Box>
        </Fade>
    );

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={() => selectedClass ? setSelectedClass(null) : window.history.back()} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                    Class Attendance
                                </Typography>
                                <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                    {selectedClass ? `Monitoring ${selectedClass.sclassName}` : 'Centralized attendance monitoring and management.'}
                                </Typography>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{mt:4}}>
                {selectedClass ? renderAttendanceView() : renderClassesView()}
            </ContentContainer>
        </PageWrapper>
    );
};

export default ClassAttendance;
